import { AlertCircle, CheckCircle, Info } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ClientS3Manager, type S3Credentials } from "@/services/s3/ClientS3Manager";
import { EncryptedS3CredentialService } from "@/services/s3/EncryptedS3CredentialService";

interface S3ConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  craftspaceId: string;
  craftspaceName?: string;
  deferCredentialSave?: boolean;
  initialConnection?: {
    provider?: string;
    bucketName?: string;
    region?: string;
    endpoint?: string;
    connectionName?: string;
  };
  onConnectionComplete?: (connectionData: {
    craftspaceName: string;
    provider: string;
    bucketName: string;
    region: string;
    endpoint: string;
    accessKeyId?: string;
    secretAccessKey?: string;
  }) => void;
}

interface S3Provider {
  id: string;
  name: string;
  endpoint: string;
  region: string;
  icon?: React.ReactNode;
  locations?: { id: string; name: string; endpoint: string; region: string }[];
}

const s3Providers: S3Provider[] = [
  {
    id: "aws",
    name: "Amazon S3",
    endpoint: "https://s3.amazonaws.com",
    region: "us-east-1",
  },
  {
    id: "minio",
    name: "MinIO",
    endpoint: "",
    region: "us-east-1",
  },
  {
    id: "hetzner",
    name: "Hetzner Cloud Storage",
    endpoint: "https://fsn1.your-objectstorage.com",
    region: "eu-central",
    locations: [
      {
        id: "fsn1",
        name: "Falkenstein",
        endpoint: "https://fsn1.your-objectstorage.com",
        region: "eu-central",
      },
      {
        id: "nbg1",
        name: "Nuremberg",
        endpoint: "https://nbg1.your-objectstorage.com",
        region: "eu-central",
      },
      {
        id: "hel1",
        name: "Helsinki",
        endpoint: "https://hel1.your-objectstorage.com",
        region: "eu-central",
      },
    ],
  },
  {
    id: "digitalocean",
    name: "DigitalOcean Spaces",
    endpoint: "https://nyc3.digitaloceanspaces.com",
    region: "nyc3",
  },
  {
    id: "backblaze",
    name: "Backblaze B2",
    endpoint: "https://s3.us-west-002.backblazeb2.com",
    region: "us-west-002",
  },
  {
    id: "cloudflare",
    name: "Cloudflare R2",
    endpoint: "https://accountid.r2.cloudflarestorage.com",
    region: "auto",
  },
  {
    id: "wasabi",
    name: "Wasabi",
    endpoint: "https://s3.wasabisys.com",
    region: "us-east-1",
  },
  {
    id: "custom",
    name: "Custom S3-Compatible",
    endpoint: "",
    region: "us-east-1",
  },
];

export const S3ConnectionDialog: React.FC<S3ConnectionDialogProps> = ({
  open,
  onClose,
  craftspaceId,
  craftspaceName,
  deferCredentialSave = false,
  initialConnection,
  onConnectionComplete,
}) => {
  const { toast } = useToast();
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [endpoint, setEndpoint] = useState("");
  const [region, setRegion] = useState("");
  const [bucketName, setBucketName] = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [_connectionName, setConnectionName] = useState("");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [_showSetupGuide, _setShowSetupGuide] = useState(false);
  const [showCorsStep, setShowCorsStep] = useState(false);
  const [connectionTested, setConnectionTested] = useState(false);
  const [corsCommand, setCorsCommand] = useState("");
  const [corsJson, setCorsJson] = useState("");

  const resolveProviderId = useCallback((provider?: string): string => {
    if (!provider) return "";
    const normalized = provider.trim().toLowerCase();
    if (s3Providers.some((item) => item.id === normalized)) {
      return normalized;
    }
    const byName = s3Providers.find((item) => item.name.toLowerCase() === normalized);
    if (byName) {
      return byName.id;
    }
    return "custom";
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!initialConnection) {
      return;
    }

    const providerId = resolveProviderId(initialConnection.provider);
    const providerDefaults = s3Providers.find((item) => item.id === providerId);

    setSelectedProvider(providerId);
    setEndpoint(initialConnection.endpoint || providerDefaults?.endpoint || "");
    setRegion(initialConnection.region || providerDefaults?.region || "");
    setBucketName(initialConnection.bucketName || "");
    setConnectionName(initialConnection.connectionName || initialConnection.bucketName || "");
    setAccessKey("");
    setSecretKey("");
    setShowCorsStep(false);
    setConnectionTested(false);
  }, [initialConnection, open, resolveProviderId]);

  // Generate CORS command with user's credentials
  const _generateCorsSteps = () => {
    const corsConfig = [
      {
        AllowedOrigins: [
          "http://localhost:3000",
          "http://localhost:4173",
          "http://localhost:5173",
          "http://localhost:5174",
          "http://localhost:5175",
          "http://localhost:8080",
          "http://127.0.0.1:3000",
          "http://127.0.0.1:4173",
          "http://127.0.0.1:8080",
          "https://360crafter.com",
          "https://app.360crafter.com",
        ],
        AllowedMethods: ["GET", "PUT", "POST", "DELETE", "HEAD"],
        AllowedHeaders: ["*"],
        ExposeHeaders: ["ETag", "x-amz-meta-*"],
        MaxAgeSeconds: 3000,
      },
    ];
    return [
      {
        step: 1,
        title: "Configure AWS CLI",
        description: "Set up AWS CLI with your Hetzner credentials",
        commands: [
          `aws configure set aws_access_key_id ${accessKey} --profile hetzner`,
          `aws configure set aws_secret_access_key ${secretKey} --profile hetzner`,
          `aws configure set region us-east-1 --profile hetzner`,
        ],
      },
      {
        step: 2,
        title: "Create CORS Configuration",
        description: "Create the CORS configuration file",
        commands: [
          `echo '${JSON.stringify({ CORSRules: corsConfig })}' > cors-config.json`,
        ],
      },
      {
        step: 3,
        title: "Apply CORS Configuration",
        description: "Apply CORS settings to your bucket",
        commands: [
          `aws s3api put-bucket-cors --bucket ${bucketName} --cors-configuration file://cors-config.json --endpoint-url ${endpoint} --profile hetzner`,
        ],
      },
      {
        step: 4,
        title: "Verify CORS Configuration",
        description: "Check that CORS was applied successfully",
        commands: [
          `aws s3api get-bucket-cors --bucket ${bucketName} --endpoint-url ${endpoint} --profile hetzner`,
        ],
      },
      {
        step: 5,
        title: "Clean Up",
        description: "Remove the temporary configuration file",
        commands: [`rm cors-config.json`],
      },
    ];
  };

  const _copyCorsStep = async (commands: string[]) => {
    try {
      const commandText = commands.join("\n");
      await navigator.clipboard.writeText(commandText);
      toast({
        title: "Copied!",
        description: "Commands copied to clipboard.",
      });
    } catch (_error) {
      toast({
        variant: "destructive",
        title: "Copy Failed",
        description: "Could not copy to clipboard. Please select and copy manually.",
      });
    }
  };

  // Validation functions
  const _validateBucketName = (name: string): boolean => {
    const bucketNameRegex = /^[a-z0-9][a-z0-9.-]*[a-z0-9]$/;
    return bucketNameRegex.test(name) && name.length >= 3 && name.length <= 63;
  };

  const getBucketNameError = (name: string): string | null => {
    if (!name) return null;
    if (name.length < 3) return "Bucket name must be at least 3 characters";
    if (name.length > 63) return "Bucket name must be 63 characters or less";
    if (!/^[a-z0-9]/.test(name)) return "Bucket name must start with a letter or number";
    if (!/[a-z0-9]$/.test(name)) return "Bucket name must end with a letter or number";
    if (!/^[a-z0-9.-]*$/.test(name))
      return "Bucket name can only contain lowercase letters, numbers, dots, and hyphens";
    if (/\.{2,}/.test(name)) return "Bucket name cannot contain consecutive dots";
    if (/^-/.test(name) || /-$/.test(name)) return "Bucket name cannot start or end with a hyphen";
    return null;
  };

  const parseEndpointInput = (input: string): { endpoint: string; bucketName?: string } => {
    const trimmed = input.trim();
    if (!trimmed) {
      return { endpoint: "" };
    }

    try {
      const normalized = trimmed.startsWith("http://") || trimmed.startsWith("https://")
        ? trimmed
        : `https://${trimmed}`;
      const parsed = new URL(normalized);
      const path = parsed.pathname.replace(/\/+$/, "");
      const pathParts = path.split("/").filter(Boolean);
      const bucketFromPath = pathParts.length === 1 ? pathParts[0] : undefined;
      const cleanedEndpoint = `${parsed.protocol}//${parsed.host}`;
      return { endpoint: cleanedEndpoint, bucketName: bucketFromPath };
    } catch {
      return { endpoint: trimmed };
    }
  };

  const handleProviderChange = (providerId: string) => {
    setSelectedProvider(providerId);
    const provider = s3Providers.find((p) => p.id === providerId);
    if (provider) {
      if (providerId === "hetzner") {
        const defaultLocation = provider.locations?.[0];
        if (defaultLocation) {
          setEndpoint(defaultLocation.endpoint);
          setRegion(defaultLocation.region);
        }
      } else {
        setEndpoint(provider.endpoint);
        setRegion(provider.region);
      }
    }
  };

  const testConnection = async () => {
    if (!endpoint || !bucketName || !accessKey || !secretKey) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields.",
      });
      return;
    }

    if (getBucketNameError(bucketName)) {
      toast({
        variant: "destructive",
        title: "Invalid Bucket Name",
        description: getBucketNameError(bucketName),
      });
      return;
    }

    setTesting(true);
    try {
      const credentials: S3Credentials = {
        accessKeyId: accessKey,
        secretAccessKey: secretKey,
        endpoint,
        region,
        bucketName,
        useCorsProxy: false,
      };

      const s3Manager = new ClientS3Manager(credentials);
      const result = await s3Manager.testConnection();

      if (result.success) {
        setConnectionTested(true);
        setShowCorsStep(true);
        toast({
          title: "Connection Successful",
          description: "Connected. Configure CORS to allow browser access.",
        });
      } else {
        // Even if connection fails due to CORS, show the CORS step
        setConnectionTested(false);
        setShowCorsStep(true);
        toast({
          title: "Connection Failed",
          description: "Most often caused by missing CORS or incorrect endpoint/bucket.",
          variant: "destructive",
        });
      }
    } catch (_error: unknown) {
      // Even if connection fails due to CORS, show the CORS step
      setConnectionTested(false);
      setShowCorsStep(true);
      toast({
        title: "Connection Failed",
        description: "Most often caused by missing CORS or incorrect endpoint/bucket.",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  const generateCorsOnly = () => {
    if (!endpoint || !bucketName || !accessKey || !secretKey) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields first.",
      });
      return;
    }

    if (getBucketNameError(bucketName)) {
      toast({
        variant: "destructive",
        title: "Invalid Bucket Name",
        description: getBucketNameError(bucketName),
      });
      return;
    }

    const steps = _generateCorsSteps();
    const commandText = steps
      .flatMap((step) => step.commands)
      .join("\n");
    const jsonConfig = JSON.stringify(
      [
        {
          AllowedOrigins: [
            "http://localhost:3000",
            "http://localhost:4173",
            "http://localhost:5173",
            "http://localhost:5174",
            "http://localhost:5175",
            "http://localhost:8080",
            "http://127.0.0.1:3000",
            "http://127.0.0.1:4173",
            "http://127.0.0.1:8080",
            "https://360crafter.com",
            "https://app.360crafter.com",
          ],
          AllowedMethods: ["GET", "PUT", "POST", "DELETE", "HEAD"],
          AllowedHeaders: ["*"],
          ExposeHeaders: ["ETag", "x-amz-meta-*"],
          MaxAgeSeconds: 3000,
        },
      ],
      null,
      2,
    );
    setCorsCommand(commandText);
    setCorsJson(jsonConfig);
    setShowCorsStep(true);
    setConnectionTested(false);
    toast({
      title: "CORS Command Generated",
      description: "CORS command is ready. Copy and run it in your terminal.",
    });
  };

  const saveConnection = async () => {
    if (!selectedProvider || !endpoint || !bucketName || !accessKey || !secretKey) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields.",
      });
      return;
    }

    if (getBucketNameError(bucketName)) {
      toast({
        variant: "destructive",
        title: "Invalid Bucket Name",
        description: getBucketNameError(bucketName),
      });
      return;
    }

    setSaving(true);
    try {
      const credentials: S3Credentials = {
        accessKeyId: accessKey,
        secretAccessKey: secretKey,
        endpoint,
        region,
        bucketName,
        useCorsProxy: false,
      };

      const s3Manager = new ClientS3Manager(credentials);
      const testResult = await s3Manager.testConnection();

      if (!testResult.success) {
        throw new Error(testResult.error || "Connection test failed");
      }

      if (!deferCredentialSave) {
        if (!craftspaceId) {
          throw new Error("Missing craftspace ID for saving credentials");
        }
        await EncryptedS3CredentialService.storeCredentials(craftspaceId, credentials);
      }

      toast({
        title: "Connection Saved",
        description: "Your S3 connection has been saved successfully.",
      });

      onConnectionComplete?.({
        craftspaceName: craftspaceName || bucketName,
        provider: selectedProvider,
        bucketName: bucketName,
        region: region,
        endpoint: endpoint,
        accessKeyId: deferCredentialSave ? accessKey : undefined,
        secretAccessKey: deferCredentialSave ? secretKey : undefined,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to save S3 connection";
      toast({
        variant: "destructive",
        title: "Save Failed",
        description: message,
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSelectedProvider("");
    setEndpoint("");
    setRegion("");
    setBucketName("");
    setAccessKey("");
    setSecretKey("");
    setConnectionName("");
    setShowCorsStep(false);
    setConnectionTested(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Connect Custom S3 Storage</DialogTitle>
          <DialogDescription className="text-gray-300">
            Configure your own S3-compatible storage for this craftspace.
          </DialogDescription>
        </DialogHeader>

        {/* CORS Configuration Step */}
        {showCorsStep && (
          <div className="border border-gray-700 bg-gray-900/60 p-4 rounded-lg mb-4">
            <div className="flex items-center mb-2">
              <Info className="w-5 h-5 mr-2 text-gray-300" />
              <h3 className="font-semibold text-lg text-white">
                {connectionTested ? "Configure CORS" : "Next Step"}
              </h3>
            </div>
            <p className="text-sm text-gray-300 mb-3">
              {connectionTested
                ? "Allow browser access by configuring CORS on your bucket."
                : "Most failures here are CORS or endpoint/bucket mismatch."}
            </p>

            {selectedProvider === "hetzner" && (
              <p className="text-sm text-red-300 mb-3">
                Hetzner does not support CORS in the browser. Use another provider or a
                server-side proxy.
              </p>
            )}

            <div className="text-sm text-gray-300 space-y-1 mb-4">
              <div>1. Endpoint: account URL only (no bucket path).</div>
              <div>2. Bucket name: set separately.</div>
              <div>3. Add CORS on the bucket for your app origin.</div>
            </div>

            {corsCommand && (
              <div className="mb-4">
                <Label className="text-white text-sm">CORS Command</Label>
                <pre className="mt-2 p-3 text-xs bg-gray-950 border border-gray-800 rounded text-gray-200 whitespace-pre-wrap">
{corsCommand}
                </pre>
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => _copyCorsStep(corsCommand.split("\n"))}
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Copy Command
                  </Button>
                </div>
              </div>
            )}

            {corsJson && (
              <div className="mb-4">
                <Label className="text-white text-sm">CORS JSON (Paste in Console)</Label>
                <pre className="mt-2 p-3 text-xs bg-gray-950 border border-gray-800 rounded text-gray-200 whitespace-pre-wrap">
{corsJson}
                </pre>
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => _copyCorsStep(corsJson.split("\n"))}
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Copy JSON
                  </Button>
                </div>
              </div>
            )}

            <div className="flex space-x-2">
              <Button
                onClick={() => setShowCorsStep(false)}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                Back to Connection
              </Button>
              <Button
                onClick={saveConnection}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? "Saving..." : "Continue to Save Connection"}
              </Button>
            </div>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveConnection();
          }}
          className="space-y-4"
        >
          {/* S3 Provider Selection */}
          <div className="space-y-2">
            <Label className="text-white">S3 Provider</Label>
            <Select value={selectedProvider} onValueChange={handleProviderChange}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                <SelectValue placeholder="Select your S3 provider" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600">
                {s3Providers.map((provider) => (
                  <SelectItem
                    key={provider.id}
                    value={provider.id}
                    className="text-white hover:bg-gray-700"
                  >
                    <span>{provider.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hetzner Location Selection */}
          {selectedProvider === "hetzner" && (
            <div className="space-y-2">
              <Label className="text-white">Hetzner Location</Label>
              <Select
                value={endpoint}
                onValueChange={(locationEndpoint) => {
                  setEndpoint(locationEndpoint);
                  const location = s3Providers
                    .find((p) => p.id === "hetzner")
                    ?.locations?.find((l) => l.endpoint === locationEndpoint);
                  if (location) {
                    setRegion(location.region);
                  }
                }}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue placeholder="Select your Hetzner location" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {s3Providers
                    .find((p) => p.id === "hetzner")
                    ?.locations?.map((location) => (
                      <SelectItem
                        key={location.id}
                        value={location.endpoint}
                        className="text-white hover:bg-gray-700"
                      >
                        <span>{location.name}</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Endpoint */}
          <div className="space-y-2">
            <Label htmlFor="endpoint" className="text-white">
              Endpoint URL
            </Label>
            <Input
              id="endpoint"
              value={endpoint}
              onChange={(e) => {
                const parsed = parseEndpointInput(e.target.value);
                setEndpoint(parsed.endpoint);
                if (parsed.bucketName) {
                  setBucketName(parsed.bucketName);
                }
              }}
              placeholder="https://s3.amazonaws.com"
              className={`bg-gray-800 border-gray-600 text-white ${
                selectedProvider === "hetzner" ? "opacity-50 cursor-not-allowed" : ""
              }`}
              readOnly={selectedProvider === "hetzner"}
            />
            {selectedProvider === "hetzner" && (
              <p className="text-gray-400 text-xs">
                Endpoint is automatically set based on your selected Hetzner location.
              </p>
            )}
          </div>

          {/* Region */}
          <div className="space-y-2">
            <Label htmlFor="region" className="text-white">
              Region
            </Label>
            <Input
              id="region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="us-east-1"
              className={`bg-gray-800 border-gray-600 text-white ${
                selectedProvider === "hetzner" ? "opacity-50 cursor-not-allowed" : ""
              }`}
              readOnly={selectedProvider === "hetzner"}
            />
            {selectedProvider === "hetzner" && (
              <p className="text-gray-400 text-xs">
                Region is automatically set to eu-central for Hetzner Cloud Storage.
              </p>
            )}
          </div>

          {/* Bucket Name */}
          <div className="space-y-2">
            <Label htmlFor="bucket-name" className="text-white">
              Bucket Name
            </Label>
            <Input
              id="bucket-name"
              value={bucketName}
              onChange={(e) => setBucketName(e.target.value)}
              placeholder="my-360builder-bucket"
              className={`bg-gray-800 border-gray-600 text-white ${
                bucketName && getBucketNameError(bucketName) ? "border-red-500" : ""
              }`}
            />
            {bucketName && getBucketNameError(bucketName) && (
              <p className="text-red-400 text-sm flex items-center">
                <AlertCircle className="w-3 h-3 mr-1" />
                {getBucketNameError(bucketName)}
              </p>
            )}
            {bucketName && !getBucketNameError(bucketName) && (
              <p className="text-green-400 text-sm flex items-center">
                <CheckCircle className="w-3 h-3 mr-1" />
                Valid bucket name
              </p>
            )}
          </div>

          {/* Access Key */}
          <div className="space-y-2">
            <Label htmlFor="access-key" className="text-white">
              Access Key ID
            </Label>
            <Input
              id="access-key"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
              placeholder="AKIAIOSFODNN7EXAMPLE"
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          {/* Secret Key */}
          <div className="space-y-2">
            <Label htmlFor="secret-key" className="text-white">
              Secret Access Key
            </Label>
            <Input
              id="secret-key"
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
              className="bg-gray-800 border-gray-600 text-white"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={generateCorsOnly}
              disabled={!endpoint || !bucketName || !accessKey || !secretKey}
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Generate CORS Command
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={testConnection}
              disabled={testing || !endpoint || !bucketName || !accessKey || !secretKey}
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              {testing ? "Testing..." : "Test Connection"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </Button>
            {!showCorsStep && (
              <Button
                type="submit"
                disabled={
                  saving ||
                  !selectedProvider ||
                  !endpoint ||
                  !bucketName ||
                  !accessKey ||
                  !secretKey
                }
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? "Saving..." : "Save Connection"}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
